import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@p2p/shared/domain';
import { catchError, filter, interval, map, of, switchMap, take, tap } from 'rxjs';

import { NotificationsDataService } from '../infrastructure/notifications.data.service';

import * as NotificationsActions from './notifications.actions';
import { NotificationsFacade } from './notifications.facade';
import { NotificationsPageParams } from './notifications.model';

@Injectable()
export class NotificationsEffects {
    private readonly actions$ = inject(Actions);
    private readonly notificationsFacade = inject(NotificationsFacade);
    private readonly notificationsDataService = inject(NotificationsDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly translocoService = inject(TranslocoService);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.loadNotificationsList),
            switchMap(({ pageParams: newPageParams }) =>
                this.notificationsFacade.pageParams$.pipe(
                    map((oldPageParams) => {
                        const pageParams: NotificationsPageParams = { ...oldPageParams, ...newPageParams };

                        Object.keys(pageParams).forEach((key) => {
                            const paramsKey = key as keyof NotificationsPageParams;

                            if (pageParams[paramsKey] == undefined || pageParams[paramsKey] === null) {
                                delete pageParams[paramsKey];
                            }
                        });

                        return pageParams;
                    }),
                    take(1)
                )
            ),
            tap((pageParams) => this.notificationsFacade.setPageParams(pageParams)),
            switchMap((pageParams) =>
                this.notificationsDataService.getNotifications(pageParams).pipe(
                    map((response) => NotificationsActions.loadNotificationsListSuccess({ response })),
                    catchError((error: HttpErrorResponse) =>
                        of(NotificationsActions.loadNotificationsListFailure({ error }))
                    )
                )
            )
        )
    );

    makeRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.makeNotificationRead),
            switchMap(({ id }) =>
                this.notificationsDataService.makeRead(id).pipe(
                    map(() => {
                        this.notificationsFacade.loadUnreadCount();
                        return NotificationsActions.makeNotificationReadSuccess({ id });
                    }),
                    catchError(() => {
                        this.notificationService.open(
                            this.translocoService.translate('notifications.Notifications.MakeRead.Failure')
                        );

                        return of(NotificationsActions.makeNotificationReadFailure({ id }));
                    })
                )
            )
        )
    );

    loadUnreadCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsActions.loadUnreadCount),
            switchMap(() =>
                this.notificationsDataService.getNotificationsUnreadCount().pipe(
                    map((response) => NotificationsActions.loadUnreadCountSuccess(response)),
                    catchError((error: HttpErrorResponse) => of(NotificationsActions.loadUnreadCountFailure({ error })))
                )
            )
        )
    );

    startUnreadCountPolling$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(NotificationsActions.startUnreadCountPolling),
                switchMap(() =>
                    interval(5000).pipe(
                        switchMap(() =>
                            this.notificationsFacade.unreadCountLoading$.pipe(
                                filter((loading) => !loading),
                                tap(() => this.notificationsFacade.loadUnreadCount()),
                                take(1)
                            )
                        )
                    )
                )
            ),
        { dispatch: false }
    );
}
